"use client";

import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Filter, ChevronDown } from "lucide-react";
import { ResponsivePopover } from "@/components/ui/responsive-popover";

// Types
import type {
  TimelineFilters,
  TimelineSortOptions,
} from "./types/timeline-filters";

// Hooks
import { useTimelineFilterState } from "./hooks/useTimelineFilterState";
import { useTimelineFilterLabels } from "./hooks/useTimelineFilterLabels";

// Sub-components
import { TimelineFilterSearch } from "./components/TimelineFilterSearch";
import { TimelineFilterSortControls } from "./components/TimelineFilterSortControls";
import { TimelineFilterHeader } from "./components/TimelineFilterHeader";
import { TimelineFilterTypeSection } from "./components/TimelineFilterTypeSection";
import { TimelineFilterStatusSection } from "./components/TimelineFilterStatusSection";
import { TimelineFilterDirectionSection } from "./components/TimelineFilterDirectionSection";
import { TimelineFilterDateRange } from "./components/TimelineFilterDateRange";
import { TimelineActiveFilters } from "./components/TimelineActiveFilters";

interface TimelineFiltersProps {
  filters: TimelineFilters;
  sort: TimelineSortOptions;
  onFiltersChange: (filters: TimelineFilters) => void;
  onSortChange: (sort: TimelineSortOptions) => void;
  className?: string;
}

const TimelineFiltersComponentBase = ({
  filters,
  sort,
  onFiltersChange,
  onSortChange,
  className = "",
}: TimelineFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Use filter state hook
  const { localSearch, handleSearchChange, clearAllFilters } =
    useTimelineFilterState({
      filters,
      onFiltersChange,
    });

  // Use filter labels hook
  const { activeFilterCount, activeFilterLabels } =
    useTimelineFilterLabels(filters);

  // Sort handlers
  const handleSortFieldChange = useCallback(
    (value: string) => {
      const isSortField = (v: string): v is TimelineSortOptions["field"] =>
        v === "occurred_at" || v === "created_at" || v === "title" || v === "type";
      if (isSortField(value)) {
        onSortChange({ ...sort, field: value });
      }
    },
    [sort, onSortChange]
  );

  const handleSortDirectionToggle = useCallback(() => {
    onSortChange({
      ...sort,
      direction: sort.direction === "asc" ? "desc" : "asc",
    });
  }, [sort, onSortChange]);

  // Filter toggle handlers
  const toggleTypeFilter = useCallback(
    (type: string) => {
      const currentTypes = filters.type || [];
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter((t) => t !== type)
        : [...currentTypes, type];

      onFiltersChange({
        ...filters,
        type: newTypes.length > 0 ? newTypes : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const toggleStatusFilter = useCallback(
    (status: string) => {
      const currentStatuses = filters.status || [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter((s) => s !== status)
        : [...currentStatuses, status];

      onFiltersChange({
        ...filters,
        status: newStatuses.length > 0 ? newStatuses : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const toggleDirectionFilter = useCallback(
    (direction: string) => {
      const currentDirections = filters.direction || [];
      const newDirections = currentDirections.includes(direction)
        ? currentDirections.filter((d) => d !== direction)
        : [...currentDirections, direction];

      onFiltersChange({
        ...filters,
        direction: newDirections.length > 0 ? newDirections : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const handleDateFromChange = useCallback(
    (date: string | undefined) => {
      onFiltersChange({
        ...filters,
        dateFrom: date,
      });
    },
    [filters, onFiltersChange]
  );

  const handleDateToChange = useCallback(
    (date: string | undefined) => {
      onFiltersChange({
        ...filters,
        dateTo: date,
      });
    },
    [filters, onFiltersChange]
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Filter Controls */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50 to-white">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search Input */}
            <TimelineFilterSearch
              value={localSearch}
              onChange={handleSearchChange}
            />

            {/* Sort and Filter Controls */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Sort Controls */}
              <TimelineFilterSortControls
                sort={sort}
                onSortFieldChange={handleSortFieldChange}
                onSortDirectionToggle={handleSortDirectionToggle}
              />

              {/* Filter Popover */}
              <ResponsivePopover
                asChild={true}
                content={
                  <div className="w-full max-w-2xl">
                    <div className="space-y-6 p-2">
                      {/* Header */}
                      <TimelineFilterHeader
                        activeFilterCount={activeFilterCount}
                        onClearAll={clearAllFilters}
                      />

                      {/* Type Filters */}
                      <TimelineFilterTypeSection
                        filters={filters}
                        onToggleType={toggleTypeFilter}
                      />

                      <Separator />

                      {/* Status Filters */}
                      <TimelineFilterStatusSection
                        filters={filters}
                        onToggleStatus={toggleStatusFilter}
                      />

                      <Separator />

                      {/* Direction and Date Filters */}
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Direction Filters */}
                        <TimelineFilterDirectionSection
                          filters={filters}
                          onToggleDirection={toggleDirectionFilter}
                        />

                        {/* Date Range */}
                        <TimelineFilterDateRange
                          filters={filters}
                          onDateFromChange={handleDateFromChange}
                          onDateToChange={handleDateToChange}
                        />
                      </div>
                    </div>
                  </div>
                }
                title="Timeline Filters"
                className="min-w-[600px]"
              >
                <Button
                  variant={activeFilterCount > 0 ? "default" : "outline"}
                  size="sm"
                  className={`h-9 transition-all ${
                    activeFilterCount > 0
                      ? "bg-blue-600 text-white shadow-md hover:bg-blue-700"
                      : "hover:bg-card-50"
                  }`}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Filters</span>
                  <span className="sm:hidden">Filter</span>
                  {activeFilterCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-2 bg-card text-blue-600 font-semibold"
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </ResponsivePopover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Filters Display */}
      <TimelineActiveFilters
        activeFilterCount={activeFilterCount}
        activeFilterLabels={activeFilterLabels}
        onClearAll={clearAllFilters}
      />
    </div>
  );
};

export const TimelineFiltersComponent = React.memo(
  TimelineFiltersComponentBase
);
